// Settings → Payments: the reference's Account → Payment methods page (the
// PAYMENT METHODS block in Dens N Dente Tailwind.dc.html) as a Settings tab.
// A card face for the on-account line and one per saved Stripe card; removing
// asks first; "Add a card" (its button sits in the page's tab row) opens the
// existing Stripe Elements form. Not built: a default card (the reference's
// "Default card" pill and "Set default" — the backend has no endpoint for
// Stripe's default payment method) and the faces' 3D tilt + glare.
import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateStripeCustomerId } from "store/slices/userSlice";

import api, { NO_RETRY } from "api/api";
import endpoint from "api/endpoints";
import { ErrorMessage, Image, Loading, Toast } from "common";

import AddPaymentMethod from "../checkout/AddPaymentMethod";
import StripeWrapper from "../stripe/StripeWrapper";

import { Button, Dialog, Icon, Skeleton } from "@/design-system";

// The reference's `_brandLabel` and `_faceBg`.
const BRAND_LABEL = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "Amex",
  discover: "Discover",
};
const FACE_BG = {
  visa: "linear-gradient(150deg, var(--blue-600), var(--blue-800))",
  mastercard: "linear-gradient(150deg, var(--neutral-700), var(--neutral-900))",
  amex: "linear-gradient(150deg, var(--blue-400), var(--blue-600))",
};
const OTHER_FACE_BG =
  "linear-gradient(150deg, var(--neutral-600), var(--neutral-800))";

const brandLabel = (brand) => BRAND_LABEL[brand] || "Card";

const FACE =
  "relative overflow-hidden aspect-[1.586] rounded-[18px] p-[20px] flex flex-col text-white shadow-md";
const CHIP =
  "w-[38px] h-[27px] rounded-[6px] [background:linear-gradient(135deg,_rgba(255,255,255,.55),_rgba(255,255,255,.22))] border border-[rgba(255,255,255,.35)] shrink-0";
const FACE_LABEL = "text-2xs uppercase tracking-[var(--tracking-wide)]";
const FACE_VALUE = "text-sm font-semibold";

function OnAccountFace({ accountNumber, holder, termsName }) {
  return (
    <div
      className={`${FACE} [background:linear-gradient(150deg,_var(--blue-800),_var(--blue-900))]`}
    >
      <Image
        src="/logo-mark-white.png"
        alt=""
        aria-hidden="true"
        className="absolute right-[-26px] bottom-[-32px] w-[150px] opacity-[.09] pointer-events-none"
      />
      <div className="relative flex items-start justify-between gap-[var(--space-2)]">
        <span className="inline-flex items-center gap-[7px]">
          <Icon
            name="verified"
            size={19}
            fill
            weight={400}
            className="text-blue-200"
          />
          <span className="font-semibold text-md leading-none">On account</span>
        </span>
        <span className="text-2xs font-bold uppercase tracking-[var(--tracking-wide)] bg-orange-500 text-white py-[3px] px-[9px] rounded-pill">
          Default
        </span>
      </div>
      <div className="relative flex items-center gap-[10px] mt-auto mb-[12px]">
        <span className={CHIP} />
        <Icon name="contactless" size={20} className="text-blue-200" />
      </div>
      <div className="relative text-lg tracking-[.06em] leading-none mb-[12px]">
        {accountNumber}
      </div>
      <div className="relative flex items-end justify-between gap-[var(--space-3)]">
        <span className="flex flex-col gap-[2px] min-w-0">
          <span className={`${FACE_LABEL} text-blue-200`}>Account holder</span>
          <span className={`${FACE_VALUE} uppercase tracking-[.04em] truncate`}>
            {holder}
          </span>
        </span>
        <span className="flex flex-col gap-[2px] text-right shrink-0">
          <span className={`${FACE_LABEL} text-blue-200`}>Terms</span>
          {/* The customer's own NetSuite term (Oct 21 2026). The face only
              renders for customers with net terms, so it is always set. */}
          <span className={FACE_VALUE}>{termsName}</span>
        </span>
      </div>
    </div>
  );
}

function CardFace({ card, holder }) {
  const exp = `${String(card.exp_month).padStart(2, "0")}/${String(
    card.exp_year
  ).slice(-2)}`;
  // Stripe knows credit / debit / prepaid; the reference always says "Credit".
  const funding =
    card.funding && card.funding !== "unknown" ? card.funding : "credit";
  return (
    <div
      className={FACE}
      style={{ background: FACE_BG[card.brand] || OTHER_FACE_BG }}
    >
      <div className="relative flex items-start justify-between gap-[var(--space-2)]">
        <span className="text-2xs font-bold uppercase tracking-[var(--tracking-wider)] text-[rgba(255,255,255,.75)]">
          {funding}
        </span>
        <span className="italic font-bold text-[17px] tracking-[.06em] uppercase leading-none">
          {brandLabel(card.brand)}
        </span>
      </div>
      <div className="relative flex items-center gap-[10px] mt-auto mb-[12px]">
        <span className={CHIP} />
        <Icon
          name="contactless"
          size={20}
          className="text-[rgba(255,255,255,.65)]"
        />
      </div>
      <div className="relative text-lg tracking-[.06em] leading-none mb-[12px] whitespace-nowrap">
        •••• •••• •••• {card.last4}
      </div>
      <div className="relative flex items-end justify-between gap-[var(--space-3)]">
        <span className="flex flex-col gap-[2px] min-w-0">
          <span className={`${FACE_LABEL} text-[rgba(255,255,255,.7)]`}>
            Cardholder
          </span>
          <span className={`${FACE_VALUE} uppercase tracking-[.04em] truncate`}>
            {holder}
          </span>
        </span>
        <span className="flex flex-col gap-[2px] text-right shrink-0">
          <span className={`${FACE_LABEL} text-[rgba(255,255,255,.7)]`}>
            Valid thru
          </span>
          <span className={FACE_VALUE}>{exp}</span>
        </span>
      </div>
    </div>
  );
}

export default function PaymentMethodsPanel({
  clinic,
  billingEmail,
  accountNumber,
  onAccount,
  addOpen,
  onAddClose,
}) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const userInfo = useSelector((state) => state.user.info);
  const stripeCustomerId = userInfo?.stripeCustomerId;

  const [methods, setMethods] = useState(null); // null while loading
  const [error, setError] = useState("");
  const [removing, setRemoving] = useState(null); // card awaiting confirmation
  const [removeBusy, setRemoveBusy] = useState(false);
  const [setupError, setSetupError] = useState("");
  const creatingCustomer = useRef(false);

  const fetchMethods = useCallback(async () => {
    if (!stripeCustomerId) {
      setMethods([]);
      return;
    }
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get(
        endpoint.GET_PAYMENT_METHODS(stripeCustomerId),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMethods(res.data?.paymentMethods || []);
      setError("");
    } catch {
      setError("We couldn't load your saved cards. Please try again later.");
      setMethods([]);
    }
  }, [stripeCustomerId, getAccessTokenSilently]);

  useEffect(() => {
    fetchMethods();
  }, [fetchMethods]);

  // Saving a card needs a Stripe customer; a first-timer gets one when the
  // dialog opens, under the login email (custentity1) the ownership checks use.
  // Not idempotent server-side, hence NO_RETRY and the in-flight guard.
  useEffect(() => {
    if (!addOpen || stripeCustomerId || creatingCustomer.current) return;
    if (!userInfo?.custentity1) {
      setSetupError("We couldn't find the email on your account.");
      return;
    }
    creatingCustomer.current = true;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.post(
          endpoint.POST_CREATE_STRIPE_CUSTOMER(),
          {
            email: userInfo.custentity1,
            name:
              [userInfo.firstname, userInfo.lastname]
                .filter(Boolean)
                .join(" ") || clinic,
          },
          { headers: { Authorization: `Bearer ${token}` }, ...NO_RETRY }
        );
        if (res.data?.customerId) {
          dispatch(updateStripeCustomerId(res.data.customerId));
        }
      } catch {
        setSetupError("We couldn't set up card payments. Please try again.");
      } finally {
        creatingCustomer.current = false;
      }
    })();
  }, [
    addOpen,
    stripeCustomerId,
    userInfo,
    clinic,
    getAccessTokenSilently,
    dispatch,
  ]);

  const closeAdd = () => {
    setSetupError("");
    onAddClose();
  };

  const handleAdded = () => {
    closeAdd();
    fetchMethods();
    Toast.success("Card saved");
  };

  const confirmRemove = async () => {
    if (!removing) return;
    setRemoveBusy(true);
    try {
      const token = await getAccessTokenSilently();
      await api.delete(endpoint.DELETE_PAYMENT_METHOD(removing.id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMethods((prev) => (prev || []).filter((pm) => pm.id !== removing.id));
      setRemoving(null);
      Toast.success("Card removed");
    } catch {
      Toast.error("We couldn't remove that card. Please try again.");
    } finally {
      setRemoveBusy(false);
    }
  };

  const cards = methods || [];
  const showEmpty = methods !== null && !error && !onAccount && !cards.length;
  const removingLabel = removing
    ? `${brandLabel(removing.card.brand)} ending ${removing.card.last4}`
    : "";

  return (
    <div className="mt-[28px]">
      {error && (
        <div className="mb-[var(--space-4)] max-w-[720px]">
          <ErrorMessage message={error} />
        </div>
      )}

      {/* No net terms → no On-account face. Say how to get one rather than
          leave the customer guessing why checkout is card-only. */}
      {!onAccount && methods !== null && !error && (
        <div className="mb-[var(--space-4)] flex items-start gap-[var(--space-3)] rounded-md border border-blue-100 bg-soft px-[var(--space-4)] py-[14px] max-w-[720px]">
          <Icon name="info" size={20} fill className="shrink-0 text-blue-600" />
          <div className="text-sm text-blue-900">
            Your account doesn&apos;t have payment terms yet, so orders are paid
            by card. Ask your sales rep to set up on-account terms.
          </div>
        </div>
      )}

      {showEmpty ? (
        <div className="flex items-center gap-[14px] border-[1.5px] border-dashed border-line rounded-lg py-[14px] px-[14px] bg-neutral-50 max-w-[720px]">
          <span className="w-[40px] h-[40px] rounded-full bg-card border border-line-subtle inline-flex items-center justify-center shrink-0">
            <Icon name="credit_card" size={20} className="text-ink-3" />
          </span>
          <div className="flex-1">
            <div className="font-semibold text-sm">No saved cards yet</div>
            <div className="text-sm text-ink-2">
              Add one here or at checkout for faster ordering.
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,_minmax(300px,_1fr))] gap-[var(--space-5)] max-w-[var(--container-lg)]">
          {onAccount && (
            <div className="flex flex-col gap-[10px]">
              <OnAccountFace
                accountNumber={accountNumber}
                holder={clinic}
                termsName={userInfo?.termsname}
              />
              <div className="text-xs text-ink-3">
                Nothing due at checkout · statements to {billingEmail} on the
                1st.
              </div>
            </div>
          )}

          {methods === null
            ? [0, 1].map((i) => (
                <Skeleton key={i} className="aspect-[1.586] rounded-[18px]" />
              ))
            : cards.map((pm) => (
                <div key={pm.id} className="flex flex-col gap-[10px]">
                  <CardFace
                    card={pm.card}
                    holder={pm.billing_details?.name || clinic}
                  />
                  <div className="flex items-center gap-[var(--space-2)]">
                    <button
                      type="button"
                      onClick={() => setRemoving(pm)}
                      title="Remove card"
                      aria-label={`Remove ${brandLabel(pm.card.brand)} ending ${pm.card.last4}`}
                      className="w-[36px] h-[36px] shrink-0 ml-auto border border-line bg-card rounded-pill cursor-pointer inline-flex items-center justify-center text-ink-3 [transition:border-color_var(--duration-fast),color_var(--duration-fast)] hover:border-error-500 hover:text-error-600 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                    >
                      <Icon name="delete" size={18} />
                    </button>
                  </div>
                </div>
              ))}
        </div>
      )}

      <div className="flex items-center gap-[9px] mt-[22px] text-xs text-ink-3">
        <Icon name="lock" size={16} />
        Card details are encrypted and tokenized — we never store the full
        number.
      </div>

      <Dialog
        open={!!removing}
        onClose={() => !removeBusy && setRemoving(null)}
        title="Remove this card?"
        size="sm"
      >
        <div className="flex flex-col gap-[var(--space-4)] pb-[var(--space-1)]">
          <div className="flex gap-[var(--space-3)] items-start bg-error-50 border border-error-100 rounded-md py-[14px] px-[var(--space-4)]">
            <Icon
              name="credit_card_off"
              size={22}
              fill
              weight={400}
              className="text-error-600 shrink-0"
            />
            <p className="text-sm text-ink-2 my-0 mx-0">
              {removingLabel} will be removed from your account.
              {onAccount
                ? ` Your ${userInfo?.termsname || "payment"} terms aren't affected — you can add the card again anytime.`
                : " You can add the card again anytime."}
            </p>
          </div>
          <div className="flex gap-[10px] justify-end">
            <Button
              variant="ghost"
              onClick={() => setRemoving(null)}
              disabled={removeBusy}
            >
              Keep card
            </Button>
            <Button
              variant="danger"
              startIcon="delete"
              onClick={confirmRemove}
              disabled={removeBusy}
            >
              {removeBusy ? "Removing…" : "Remove card"}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog open={addOpen} onClose={closeAdd} title="Add a card" size="md">
        <div className="pb-[var(--space-2)]">
          {stripeCustomerId ? (
            <StripeWrapper>
              <AddPaymentMethod
                customerId={stripeCustomerId}
                onPaymentMethodAdded={handleAdded}
                onCancel={closeAdd}
              />
            </StripeWrapper>
          ) : setupError ? (
            <ErrorMessage message={setupError} />
          ) : (
            <Loading text="Setting up secure card storage…" />
          )}
        </div>
      </Dialog>
    </div>
  );
}
